/**
 * Terms & Conditions acceptance + proof capture.
 *
 * Two entry points:
 *   1. <terms-acceptance>  — the inline required checkbox on the cart page / cart drawer. Gates the
 *      standard Checkout button and records proof on tick.
 *   2. window.TermsGate     — an agree-popup used by "Buy it now" flows (grip Buy-it-now via
 *      grip-selector.js, and the native wallet button via the overlay below). No permanent checkbox
 *      on product pages.
 *
 * Proof is stored as cart attributes (Yes + timestamp + IP + version) that persist onto the order.
 * For the native wallet "Buy it now" (which bypasses the cart) the same values are also injected as
 * line-item properties so they land on that order too.
 */
(function () {
  function fetchConfig() {
    return (window.theme && theme.utils && theme.utils.fetchConfig)
      ? theme.utils.fetchConfig()
      : { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } };
  }
  function cartUpdateUrl() {
    return (window.theme && theme.routes && theme.routes.cart_update_url) || '/cart/update.js';
  }

  // Best-effort visitor IP welded to the click. Non-blocking — Shopify records the order's
  // browser_ip (Fraud Analysis) as an independent backup.
  function getIp() {
    var controller = new AbortController();
    var timeout = setTimeout(function () { controller.abort(); }, 2000);
    return fetch('https://api.ipify.org?format=json', { signal: controller.signal })
      .then(function (r) { return r.json(); })
      .then(function (d) { return d.ip || ''; })
      .catch(function () { return ''; })
      .finally(function () { clearTimeout(timeout); });
  }

  function writeAttributes(attributes) {
    return fetch(cartUpdateUrl(), Object.assign({}, fetchConfig(), { body: JSON.stringify({ attributes: attributes }) }))
      .catch(function (e) { console.log('[terms] write failed', e); });
  }

  // Records the core proof immediately, then welds the IP once it resolves. Returns the proof.
  async function recordProof(version) {
    var at = new Date().toISOString();
    writeAttributes({ 'Terms Accepted': 'Yes', 'Terms Accepted At': at, 'Terms Version': version || '' });
    var ip = await getIp();
    if (ip) writeAttributes({ 'Terms Accepted IP': ip });
    return { at: at, ip: ip, version: version || '' };
  }

  function clearProof() {
    writeAttributes({ 'Terms Accepted': '', 'Terms Accepted At': '', 'Terms Accepted IP': '' });
  }

  /* ---------------------------------------------------------------------------
   * 1. Cart / drawer inline checkbox
   * ------------------------------------------------------------------------- */
  if (!customElements.get('terms-acceptance')) {
    customElements.define('terms-acceptance', class extends HTMLElement {
      constructor() {
        super();
        this.checkbox = this.querySelector('.terms-acceptance__checkbox');
        this.error = this.querySelector('.terms-acceptance__error');
        this.version = this.getAttribute('data-terms-version') || '';
      }

      connectedCallback() {
        if (!this.checkbox) return;

        this.checkbox.addEventListener('change', () => {
          this.hideError();
          this.checkbox.checked ? recordProof(this.version) : clearProof();
        });

        // Block the checkout form submit when not accepted (covers the drawer's `novalidate` form).
        const form = this.checkbox.form;
        if (form && !form.dataset.termsGuard) {
          form.dataset.termsGuard = 'true';
          form.addEventListener('submit', (event) => {
            if (this.checkbox && !this.checkbox.checked) {
              event.preventDefault();
              this.showError();
              this.checkbox.focus();
            }
          });
        }
      }

      showError() { if (this.error) { this.error.hidden = false; this.error.classList.remove('hidden'); } }
      hideError() { if (this.error) { this.error.hidden = true; this.error.classList.add('hidden'); } }
    });
  }

  /* ---------------------------------------------------------------------------
   * 2. Agree-popup (TermsGate) for "Buy it now" flows
   * ------------------------------------------------------------------------- */
  var TermsGate = {
    modal: null,

    ensure: function () {
      if (!this.modal) {
        this.modal = document.querySelector('terms-modal');
        if (this.modal) this._bindDismiss();
      }
      return this.modal;
    },

    _bindDismiss: function () {
      var self = this;
      this.modal.querySelectorAll('[data-terms-close]').forEach(function (el) {
        el.addEventListener('click', function () { self.close(); });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.modal && !self.modal.hidden) self.close();
      });
    },

    // Opens the popup; calls onAgree(proof) only after the customer agrees (proof already recorded).
    require: function (onAgree) {
      var modal = this.ensure();
      if (!modal) { onAgree({ at: '', ip: '', version: '' }); return; } // no modal → don't block
      var version = modal.getAttribute('data-terms-version') || '';
      var agreeBtn = modal.querySelector('[data-terms-agree]');
      var self = this;

      // Replace the agree button to drop any handler from a previous open.
      var fresh = agreeBtn.cloneNode(true);
      agreeBtn.parentNode.replaceChild(fresh, agreeBtn);
      fresh.addEventListener('click', async function () {
        fresh.disabled = true;
        var proof = await recordProof(version);
        self.close();
        fresh.disabled = false;
        onAgree(proof);
      });

      this.open();
    },

    open: function () {
      var m = this.ensure();
      if (!m) return;
      m.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      var agree = m.querySelector('[data-terms-agree]');
      if (agree) agree.focus();
    },

    close: function () {
      var m = this.ensure();
      if (!m) return;
      m.hidden = true;
      document.documentElement.style.overflow = '';
    }
  };
  window.TermsGate = TermsGate;

  /* ---------------------------------------------------------------------------
   * 3. Native wallet "Buy it now" overlay (best-effort)
   *
   * Shopify controls this button, so we can't bind to its click. We lay a transparent overlay over
   * it: a click opens the popup; on agree we inject the proof as line-item properties (so it lands on
   * the wallet order, which bypasses the cart), remove the overlay, and re-trigger the button.
   * ------------------------------------------------------------------------- */
  function injectProofProperties(form, proof, version) {
    if (!form) return;
    var fields = {
      'Terms Accepted': 'Yes',
      'Terms Accepted At': proof.at || '',
      'Terms Accepted IP': proof.ip || '',
      'Terms Version': proof.version || version || ''
    };
    Object.keys(fields).forEach(function (key) {
      var name = 'properties[' + key + ']';
      var input = form.querySelector('input[name="' + name + '"][data-terms-prop]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.setAttribute('data-terms-prop', '');
        form.appendChild(input);
      }
      input.value = fields[key];
    });
  }

  function gateWalletButtons(root) {
    (root || document).querySelectorAll('.shopify-payment-button').forEach(function (container) {
      if (container.dataset.termsWalletGated) return;
      container.dataset.termsWalletGated = 'true';
      if (!container.style.position) container.style.position = 'relative';

      var overlay = document.createElement('div');
      overlay.className = 'terms-wallet-overlay';
      overlay.style.cssText = 'position:absolute;inset:0;z-index:5;cursor:pointer;';
      overlay.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var form = container.closest('form[data-type="add-to-cart-form"]') || container.closest('form');
        var version = TermsGate.ensure() ? TermsGate.ensure().getAttribute('data-terms-version') : '';
        TermsGate.require(function (proof) {
          injectProofProperties(form, proof, version);
          overlay.remove();
          var btn = container.querySelector('.shopify-payment-button__button');
          if (btn) { try { btn.click(); } catch (err) { /* user re-clicks; overlay is gone */ } }
        });
      });
      container.appendChild(overlay);
    });
  }

  function initWallet() { gateWalletButtons(document); }

  if (document.readyState !== 'loading') initWallet();
  else document.addEventListener('DOMContentLoaded', initWallet);

  // Catch wallet buttons injected later (quick-view) or hydrated late.
  new MutationObserver(function (muts) {
    muts.forEach(function (m) {
      m.addedNodes.forEach(function (n) {
        if (n.nodeType !== 1) return;
        if (n.classList && n.classList.contains('shopify-payment-button')) gateWalletButtons(n.parentNode || document);
        else if (n.querySelector && n.querySelector('.shopify-payment-button')) gateWalletButtons(n);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
  setTimeout(initWallet, 1500);
})();
